/**
 * Aides communes aux PDF générés dans le navigateur (devis, facture) pour qu'ils sortent de la même façon
 * sur téléphone et sur ordinateur. Demande d'Emeni (2026-11-04) : téléchargé depuis un téléphone, le devis
 * avait un logo mal affiché et des articles décalés.
 *
 * Deux causes sont traitées ici :
 *  - le logo était chargé depuis son URL dans l'iframe de capture : sur mobile le chargement réseau peut
 *    échouer ou arriver trop tard, et le logo disparaît de l'image sans erreur. On le charge donc avant la
 *    capture et on l'incorpore dans le document (data URI) ;
 *  - l'iframe de capture fait 210 mm de large, bien plus que l'écran d'un téléphone : les navigateurs
 *    mobiles y agrandissent alors le texte tout seuls (font boosting / text autosizing) et décalent les lignes
 *    du tableau. `text-size-adjust: 100%` le leur interdit.
 */

/** À insérer dans le <style> du document capturé. */
export const CSS_TAILLE_TEXTE_FIXE = 'html { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }';

const lireEnDataUri = (blob: Blob) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Lecture du logo impossible.'));
    reader.readAsDataURL(blob);
});

const chargerImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Image de capture invalide.'));
    image.src = src;
});

/**
 * Sur WebKit (Safari iPhone), html-to-image ne dessine pas le logo : l'image chargée dans le document est
 * bien là (mesurée 250×70), mais absente de la capture, sans erreur. Reproduit avec WebKit (iPhone 13) le
 * 2026-11-04 ; un passage de « chauffe » avant la capture n'y change rien. On capture donc le document SANS
 * le logo (invisible, sa place reste réservée), puis on le dessine à sa place sur le canevas, ce que tous
 * les navigateurs savent faire. Sans logo chargé, on capture normalement.
 */
export async function capturerAvecLogo(
    toPng: (el: HTMLElement, options?: Record<string, unknown>) => Promise<string>,
    element: HTMLElement,
    options: Record<string, unknown>,
): Promise<string> {
    const logo = element.querySelector<HTMLImageElement>('img[alt="Logo"]');
    if (!logo || !logo.complete || !logo.naturalWidth) return toPng(element, options);

    const zoneElement = element.getBoundingClientRect();
    const zoneLogo = logo.getBoundingClientRect();
    const visibiliteInitiale = logo.style.visibility;
    logo.style.visibility = 'hidden';
    let sansLogo: string;
    try {
        sansLogo = await toPng(element, options);
    } finally {
        logo.style.visibility = visibiliteInitiale;
    }

    const fond = await chargerImage(sansLogo);
    const canevas = document.createElement('canvas');
    canevas.width = fond.naturalWidth;
    canevas.height = fond.naturalHeight;
    const contexte = canevas.getContext('2d');
    if (!contexte) return sansLogo;
    contexte.drawImage(fond, 0, 0);
    const largeurCapturee = Number(options.width) || element.scrollWidth || zoneElement.width;
    const echelle = fond.naturalWidth / largeurCapturee;
    contexte.drawImage(
        logo,
        (zoneLogo.left - zoneElement.left) * echelle,
        (zoneLogo.top - zoneElement.top) * echelle,
        zoneLogo.width * echelle,
        zoneLogo.height * echelle,
    );
    return canevas.toDataURL('image/png');
}

/**
 * Télécharge le logo et le renvoie sous forme de data URI. En cas d'échec (hors connexion, délai dépassé),
 * renvoie l'URL d'origine : le PDF se génère alors comme avant, sans logo garanti.
 */
export async function logoEnDataUri(url: string | null | undefined, delaiMs = 8000): Promise<string | null> {
    if (!url) return null;
    if (url.startsWith('data:')) return url;
    try {
        const controleur = new AbortController();
        const minuteur = setTimeout(() => controleur.abort(), delaiMs);
        try {
            const reponse = await fetch(url, { signal: controleur.signal });
            if (!reponse.ok) return url;
            const blob = await reponse.blob();
            if (!blob.type.startsWith('image/')) return url;
            return await lireEnDataUri(blob);
        } finally {
            clearTimeout(minuteur);
        }
    } catch {
        return url;
    }
}
